import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Input, StateBlock, useToast } from '@w7/ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  cmApi,
  type VolumePercentEventType,
  type VolumePercentTm,
} from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { canEditVolumePercent, canUpdateVolumePercentRate } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import { CmPanel, CmStatus } from '@/features/catalog/CmPanel'
import { VolumeNumInput } from './VolumeNumInput'
import {
  applyActiveRateToTms,
  companyRateFromShareBp,
  hydrateTmRates,
  isSpecialEvent,
  recalcFromMinStake,
  syncMinStake,
  syncPlaceBetMinFields,
  type RecalcSource,
  type VolumeRateCell,
} from './volumePercent'

function cloneTms(tms: VolumePercentTm[]): VolumePercentTm[] {
  return tms.map((tm) => ({
    ...tm,
    betfairRates: Object.fromEntries(
      Object.entries(tm.betfairRates).map(([k, v]) => [k, { ...v }]),
    ),
  }))
}

function cellKey(agentId: string, eventName: string): string {
  return `${agentId}::${eventName}`
}

export function VolumePercentPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const me = useCmMe()
  const canEdit = canEditVolumePercent(me.data?.caller)
  const canUpdateRate = canUpdateVolumePercentRate(me.data?.caller)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [activeRate, setActiveRate] = useState(12.5)
  const [rateConfigId, setRateConfigId] = useState('')
  const [tms, setTms] = useState<VolumePercentTm[]>([])
  const [eventTypes, setEventTypes] = useState<VolumePercentEventType[]>([])
  const [editId, setEditId] = useState('')
  const [backup, setBackup] = useState<VolumePercentTm['betfairRates'] | null>(null)
  const [status, setStatus] = useState('')
  /** Blueprint v4: percent field blink green (up) / red (down). */
  const [percentBlink, setPercentBlink] = useState<Record<string, 'up' | 'down'>>({})
  const blinkTimers = useRef<Record<string, number>>({})

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 350)
    return () => window.clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    const timers = blinkTimers.current
    return () => {
      for (const id of Object.values(timers)) window.clearTimeout(id)
    }
  }, [])

  const flashPercent = (key: string, oldVal: number, newVal: number) => {
    if (oldVal === newVal) return
    const dir: 'up' | 'down' = newVal > oldVal ? 'up' : 'down'
    // Drop class first so re-blink retriggers CSS animation (blueprint v4).
    setPercentBlink((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    window.requestAnimationFrame(() => {
      setPercentBlink((prev) => ({ ...prev, [key]: dir }))
    })
    if (blinkTimers.current[key]) window.clearTimeout(blinkTimers.current[key])
    blinkTimers.current[key] = window.setTimeout(() => {
      setPercentBlink((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      delete blinkTimers.current[key]
    }, 2000)
  }

  const flashPercentDiffs = (before: VolumePercentTm[], after: VolumePercentTm[], names: string[]) => {
    for (const tm of after) {
      const prevTm = before.find((t) => t.agentId === tm.agentId)
      for (const name of names) {
        const oldP = Number(prevTm?.betfairRates[name]?.percent || 0)
        const newP = Number(tm.betfairRates[name]?.percent || 0)
        flashPercent(cellKey(tm.agentId, name), oldP, newP)
      }
    }
  }

  const blinkClassFor = (key: string): string => {
    const dir = percentBlink[key]
    if (dir === 'up') return 'cm-blink-green'
    if (dir === 'down') return 'cm-blink-red'
    return ''
  }

  const listQ = useQuery({
    queryKey: ['cm', 'volume-percent', search],
    queryFn: async () => {
      const res = await cmApi.volumePercentList(authHeaders(), search)
      const names = res.eventTypes.map((e) => e.name)
      const hydrated = applyActiveRateToTms(
        hydrateTmRates(cloneTms(res.tms), names),
        names,
        res.activeRate,
      )
      setActiveRate(res.activeRate)
      setRateConfigId(res.rateConfigId)
      setEventTypes(res.eventTypes)
      setTms(hydrated)
      return res
    },
  })

  const eventNames = useMemo(() => eventTypes.map((e) => e.name), [eventTypes])
  const showEmpty =
    !tms.length && !listQ.isLoading && !listQ.isFetching && !listQ.isError

  const patchCell = (
    agentId: string,
    eventName: string,
    patch: Partial<VolumeRateCell>,
    source: RecalcSource,
  ) => {
    setTms((prev) => {
      const nextTms = prev.map((tm) => {
        if (tm.agentId !== agentId) return tm
        const prevRow = tm.betfairRates[eventName] ?? ({} as VolumeRateCell)
        const row = { ...prevRow, ...patch }
        const next = recalcFromMinStake(
          row,
          eventName,
          tm.currencyType,
          activeRate,
          source,
          tm.myShare,
        )
        return {
          ...tm,
          betfairRates: { ...tm.betfairRates, [eventName]: next },
        }
      })
      const oldP = Number(
        prev.find((t) => t.agentId === agentId)?.betfairRates[eventName]?.percent || 0,
      )
      const newP = Number(
        nextTms.find((t) => t.agentId === agentId)?.betfairRates[eventName]?.percent || 0,
      )
      queueMicrotask(() => flashPercent(cellKey(agentId, eventName), oldP, newP))
      return nextTms
    })
  }

  const applyActiveRate = (value: number) => {
    setActiveRate(value)
    setTms((prev) => {
      const updated = applyActiveRateToTms(prev, eventNames, value)
      queueMicrotask(() => flashPercentDiffs(prev, updated, eventNames))
      return updated
    })
  }

  const save = useMutation({
    mutationFn: async (opts: { all: boolean; agentId?: string; withRate: boolean }) => {
      let payload = tms
      if (!opts.all && opts.agentId) {
        payload = tms.filter((t) => t.agentId === opts.agentId)
      }
      const prepared = payload.map((tm) => {
        const rates: Record<string, VolumeRateCell> = {}
        for (const name of eventNames) {
          let row = tm.betfairRates[name]
          if (!row) continue
          row = syncPlaceBetMinFields(
            syncMinStake(
              recalcFromMinStake(
                row,
                name,
                tm.currencyType,
                activeRate,
                'manualSave',
                tm.myShare,
              ),
              name,
              tm.currencyType,
            ),
          )
          rates[name] = {
            ...row,
            oldPercent: Number(row.apiPercent || 0),
            apiPercent: Number(row.percent || 0),
            oldMinStake: Number(row.apiMinStake || 0),
            apiMinStake: Number(row.minStake || 0),
            minBet: Number(row.minStake || 0),
            validateStack: Number(row.minStake || 0),
          }
        }
        return {
          agentId: tm.agentId,
          username: tm.username,
          currencyType: tm.currencyType,
          shareBp: tm.shareBp,
          myShare:
            companyRateFromShareBp(tm.shareBp, tm.currencyType) ||
            Number(tm.myShare ?? 0),
          betfairRates: rates,
        }
      })
      return cmApi.volumePercentSave(authHeaders(), {
        tms: prepared,
        ...(opts.withRate
          ? { activeRate, rateConfigId: rateConfigId || undefined }
          : {}),
      })
    },
    onSuccess: (_data, vars) => {
      toast.success(vars.withRate ? 'Rate & TMs updated' : 'TM rates saved')
      setStatus(vars.withRate ? 'Updated active rate and TM place %' : 'Saved TM place %')
      setEditId('')
      setBackup(null)
      void qc.invalidateQueries({ queryKey: ['cm', 'volume-percent'] })
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const onEdit = (tm: VolumePercentTm) => {
    setEditId(tm.agentId)
    setBackup(
      Object.fromEntries(Object.entries(tm.betfairRates).map(([k, v]) => [k, { ...v }])),
    )
  }

  const onCancel = (tm: VolumePercentTm) => {
    if (backup) {
      setTms((prev) =>
        prev.map((row) =>
          row.agentId === tm.agentId ? { ...row, betfairRates: backup } : row,
        ),
      )
    }
    setEditId('')
    setBackup(null)
  }

  if (listQ.isLoading && !tms.length) {
    return (
      <StateBlock icon="settings" title="Loading Volume Percent…">
        Reading Triple Master place-% / min stake via CM-API.
      </StateBlock>
    )
  }

  return (
    <CmPanel
      title="Volume Percent New"
      subtitle="Percent To Place is plain % (100 = 100%) — same as Lab Volume Percent New. Values above 100 allowed."
      actions={
        canUpdateRate ? (
          <Button
            type="button"
            variant="primary"
            busy={save.isPending}
            disabled={listQ.isError}
            onClick={() => save.mutate({ all: true, withRate: true })}
          >
            Update Rate &amp; TMs
          </Button>
        ) : null
      }
    >
      <div className="cm-toolbar">
        <form
          className="cm-toolbar-search"
          onSubmit={(e) => {
            e.preventDefault()
            setSearch(searchInput.trim())
          }}
        >
          <Input
            placeholder="Search TM name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </form>
        <label className="cm-inline-field">
          <span>Active rate</span>
          <VolumeNumInput
            className="cm-num-wide"
            value={activeRate}
            aria-label="Active Betfair rate"
            onLive={applyActiveRate}
            onCommit={applyActiveRate}
          />
        </label>
      </div>

      {listQ.isError ? (
        <div className="cm-hint" role="alert">
          <p>{(listQ.error as Error).message}</p>
          <Button
            type="button"
            variant="secondary"
            busy={listQ.isFetching}
            onClick={() => void listQ.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      {!listQ.isError ? (
        <div className="cm-table-wrap">
          <table className="cm-table">
            <thead>
              <tr>
                <th>TM</th>
                <th>Rates</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {tms.map((tm) => {
                const editing = editId === tm.agentId
                return (
                  <tr key={tm.agentId}>
                    <td className="cm-td-center">
                      {tm.username}
                      <div className="cm-hint">
                        {tm.currencyType === 'INR'
                          ? 'INR'
                          : tm.currencyType === 'POINT@1'
                            ? 'POINT@1'
                            : 'POINTS'}
                      </div>
                    </td>
                    <td>
                      <table className="cm-table cm-table-nested">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Company Rate</th>
                            <th>Percent To Place</th>
                            <th>B.F Harjeet</th>
                            <th>Company Harjeet</th>
                            <th>Min Stake</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eventTypes.map((ev) => {
                            const row = tm.betfairRates[ev.name]
                            if (!row) return null
                            const special = isSpecialEvent(ev.name)
                            const pKey = cellKey(tm.agentId, ev.name)
                            return (
                              <tr key={`${tm.agentId}-${ev.name}`}>
                                <td>{ev.name}</td>
                                <td>
                                  {/* Company Rate = Edit TM Sportsbook share % (shareBp/100). */}
                                  {row.myShare}
                                </td>
                                <td>
                                  {editing ? (
                                    <VolumeNumInput
                                      value={row.percent}
                                      blinkClass={blinkClassFor(pKey)}
                                      aria-label={`${ev.name} percent to place`}
                                      onLive={(n) =>
                                        patchCell(
                                          tm.agentId,
                                          ev.name,
                                          { percent: n },
                                          'percent',
                                        )
                                      }
                                      onCommit={(n) =>
                                        patchCell(
                                          tm.agentId,
                                          ev.name,
                                          { percent: n },
                                          'percent',
                                        )
                                      }
                                    />
                                  ) : (
                                    <span className={blinkClassFor(pKey)}>
                                      {`${row.percent} %`}
                                    </span>
                                  )}
                                </td>
                                <td>{row.bfHarjeet}</td>
                                <td>
                                  {editing ? (
                                    <VolumeNumInput
                                      value={row.companyHarjeet}
                                      aria-label={`${ev.name} company harjeet`}
                                      onLive={(n) =>
                                        patchCell(
                                          tm.agentId,
                                          ev.name,
                                          { companyHarjeet: Math.round(n) },
                                          'companyHarjeet',
                                        )
                                      }
                                      onCommit={(n) =>
                                        patchCell(
                                          tm.agentId,
                                          ev.name,
                                          { companyHarjeet: Math.round(n) },
                                          'companyHarjeet',
                                        )
                                      }
                                    />
                                  ) : (
                                    row.companyHarjeet
                                  )}
                                </td>
                                <td>
                                  {editing ? (
                                    <VolumeNumInput
                                      value={row.minStake}
                                      readOnly={special}
                                      aria-label={`${ev.name} min stake`}
                                      onLive={(n) =>
                                        patchCell(
                                          tm.agentId,
                                          ev.name,
                                          { minStake: Math.round(n) },
                                          'default',
                                        )
                                      }
                                      onCommit={(n) =>
                                        patchCell(
                                          tm.agentId,
                                          ev.name,
                                          { minStake: Math.round(n) },
                                          'default',
                                        )
                                      }
                                    />
                                  ) : (
                                    row.minStake
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </td>
                    <td className="cm-td-center">
                      {canEdit && !editId ? (
                        <Button type="button" variant="secondary" onClick={() => onEdit(tm)}>
                          Edit
                        </Button>
                      ) : null}
                      {canEdit && editing ? (
                        <div className="cm-inline-actions">
                          <Button
                            type="button"
                            variant="primary"
                            busy={save.isPending}
                            onClick={() =>
                              save.mutate({ all: false, agentId: tm.agentId, withRate: false })
                            }
                          >
                            Save
                          </Button>
                          <Button type="button" variant="secondary" onClick={() => onCancel(tm)}>
                            Cancel
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {showEmpty ? (
        <p className="cm-hint">No Triple Masters under this CM yet. Create a TM in hierarchy first.</p>
      ) : null}

      {canEdit ? (
        <div className="cm-actions">
          <Button
            type="button"
            variant="secondary"
            busy={save.isPending}
            disabled={listQ.isError || !tms.length}
            onClick={() => save.mutate({ all: true, withRate: false })}
          >
            Update All TMs
          </Button>
        </div>
      ) : null}
      <CmStatus value={status} />
    </CmPanel>
  )
}
